import React, { useEffect, useRef, useState } from "react";

import "../../../../assets/scss/pages/ticketQueue.scss";
import { useMessage } from "../../../../context/MessageContext";
import { GENERAL_ERROR_MESSAGE } from "../../../../utils/message";
import BusyLoader from "../../../service-config/HelpdeskShared/BusyLoader";
import HelpdeskFormModal from "../../../service-config/HelpdeskShared/HelpdeskFormModal";
import { ViewGroupOption } from "../queueTypes";
import { fetchViewGroups, saveViewGroups } from "../services/queueService";

interface ViewAnotherGroupModalProps {
  onClose: () => void;
  /** After Save: the groups with what is now viewed, for the Communications menu. */
  onSaved: (groups: ViewGroupOption[]) => void;
}

/**
 * Communications › Groups › View Another Group (KAN-1362): every group with its open tickets; the groups already
 * listed start checked and tagged "Viewing". The header checkbox selects / clears them all. Save (at least one
 * group) lists the checked groups under Groups; Cancel and ✕ keep the saved choice.
 */
const ViewAnotherGroupModal: React.FC<ViewAnotherGroupModalProps> = ({ onClose, onSaved }) => {
  const { setMessage } = useMessage();
  const [groups, setGroups] = useState<ViewGroupOption[]>([]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const selectAllRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let active = true;
    fetchViewGroups()
      .then((response) => {
        if (!active) return;
        if (response.success) {
          const list = response.data ?? [];
          setGroups(list);
          setSelected(new Set(list.filter((group) => group.viewing).map((group) => group.id)));
        } else {
          setMessage(response.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
        }
      })
      .catch(() => active && setMessage(GENERAL_ERROR_MESSAGE, "error"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // Once per opening: MessageProvider does not memoize setMessage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allSelected = groups.length > 0 && groups.every((group) => selected.has(group.id));
  const someSelected = !allSelected && groups.some((group) => selected.has(group.id));

  // `indeterminate` has no HTML attribute; it can only be set on the element.
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(groups.map((group) => group.id)));

  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selected.size || saving) return;
    setSaving(true);
    try {
      // In the list's (A–Z) order
      const response = await saveViewGroups(groups.filter((group) => selected.has(group.id)).map((group) => group.id));
      if (!response.success || !response.data) {
        setMessage(response.error?.message ?? GENERAL_ERROR_MESSAGE, "error");
        return;
      }
      onSaved(response.data);
      onClose();
    } catch {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <HelpdeskFormModal
        title="Groups you can see"
        size="md"
        onClose={onClose}
        onSubmit={(event) => void handleSubmit(event)}
        submitLabel={saving ? "Saving…" : "Save"}
        submitDisabled={saving || loading || selected.size === 0}
      >
        <div className="viewGroups">
          <table className="viewGroups__table" aria-label="Groups">
            <thead>
              <tr>
                <th scope="col">
                  <label className="viewGroups__check">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allSelected}
                      disabled={loading || groups.length === 0}
                      onChange={toggleAll}
                      aria-label="Select all groups"
                    />
                    <span>Groups</span>
                  </label>
                </th>
                <th scope="col" className="viewGroups__count">
                  Tickets
                </th>
              </tr>
            </thead>
            <tbody>
              {!loading && groups.length === 0 && (
                <tr>
                  <td colSpan={2} className="viewGroups__empty">
                    No groups have been set up.
                  </td>
                </tr>
              )}
              {groups.map((group) => (
                <tr key={group.id}>
                  <td>
                    <label className="viewGroups__check">
                      <input
                        type="checkbox"
                        checked={selected.has(group.id)}
                        onChange={() => toggleOne(group.id)}
                      />
                      <span className="viewGroups__name">{group.name}</span>
                      {/* The saved choice; what is checked now shows on Save */}
                      {group.viewing && <span className="viewGroups__tag">Viewing</span>}
                    </label>
                  </td>
                  <td className="viewGroups__count">{group.ticketCount} Open</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </HelpdeskFormModal>
      {(loading || saving) && <BusyLoader />}
    </>
  );
};

export default ViewAnotherGroupModal;
